import {DateTime} from 'luxon';
import moment from 'moment-timezone';
import * as c from '~/common/constants/event-origins';
import {
  EVENT_STATUS_CANCELED,
  EVENT_STATUS_DRAFT,
  EVENT_STATUS_PAST,
  EVENT_STATUS_SENDING,
  USED_AS_CARD,
  USED_AS_GIFT_CARD,
  USED_AS_INVITATION,
  USED_AS_SAVE_THE_DATE,
} from '~/common/utils/event_constants';
import {convertToIso, httpsIfyUrl} from '~/common/utils.js';

// This file intended to hold all event util functions shared across various apps

export const getUsedAsTypeName = (usedAs) => {
  // Logic should match the corresponding backend function, _get_used_as_type_name
  if (usedAs === USED_AS_SAVE_THE_DATE) {
    return 'Save the Date';
  }
  if (usedAs === USED_AS_CARD || usedAs === USED_AS_GIFT_CARD) {
    return 'eCard';
  }
  return 'Invitation';
};

export const isInvitationLikeEvent = (usedAs) =>
  [USED_AS_INVITATION, USED_AS_SAVE_THE_DATE].includes(usedAs);

export const isInvitationByUsedAs = (usedAs) => usedAs === USED_AS_INVITATION;
export const isSaveTheDateByUsedAs = (usedAs) => usedAs === USED_AS_SAVE_THE_DATE;
export const isGreetingCardByUsedAs = (usedAs) =>
  [USED_AS_CARD, USED_AS_GIFT_CARD].includes(usedAs);

export const isInOneStepCreateFlow = window.location.pathname.includes('/invitation-maker');

// ORIGIN-BASED HELPERS
export const isGreetingCard = (origin) => c.CARD_ORIGINS.includes(origin);

export const isLegacyGreetingCard = (origin) => c.LEGACY_GRC_ORIGINS.includes(origin);

export const isFabricGreetingCard = (origin) => c.FABRIC_GRC_ORIGINS.includes(origin);

export const isFabric = (origin) => c.FABRIC_EVENT_ORIGINS.includes(origin);

export const isLegacyPremium = (origin) => c.LEGACY_PREMIUM_ORIGINS.includes(origin);

export const eventAtLeast24HoursInPast = (startTime) =>
  DateTime.fromISO(convertToIso(startTime)).diffNow('hours').hours < -24;

export const eventMoreThan30DaysAgo = (startTime) =>
  DateTime.fromISO(convertToIso(startTime)).diffNow('days').days < -30;

export const eventMoreThan1WeekAway = (startTime) =>
  DateTime.fromISO(convertToIso(startTime)).diffNow('days').days > 7;

// EVENT STATUS-BASED HELPERS
export const isPast = (status) => status === EVENT_STATUS_PAST;

export const isDraft = (status) => status === EVENT_STATUS_DRAFT;
export const isSent = (status) => status === EVENT_STATUS_SENDING;

export const prettyStatus = (status) => {
  if (status === 'cancelled') {
    return EVENT_STATUS_CANCELED;
  }
  return status;
};

export const isCanceled = (status) => prettyStatus(status) === EVENT_STATUS_CANCELED;

// EVENT FLAG HELPERS
export const isRsvpOff = (rsvpOff) => !rsvpOff;

// EVENT TYPE-BASED HELPERS
// TODO: this doesn't make sense: window.mealOptionsEventTypes?.[0].includes(eventType);
// when eventType is an empty string, it checks if the first string `includes` the empty
// string, which is true. Why are we checking whether the array of characters contains
// the full event type?
export const isMealOptionsEventType = (eventType) => {
  const isMealOptionsOnViewInvitation = window.mealOptionsEventTypes?.includes(eventType);
  const isMealOptionsOnCreateFlow = window.mealOptionsEventTypes?.[0].includes(eventType);
  return !!eventType && (isMealOptionsOnViewInvitation || isMealOptionsOnCreateFlow);
};

// FABRIC ART HELPERS
export const fabricDesignRemoveDuplicateUyoEmptyImages = (design) => {
  if (!design || !design.objects) return design;
  const updated = {...design};

  const uyoImages = updated.objects.filter((obj) => obj && obj.type === 'uYOImage');
  const hasDuplications = uyoImages.reduce((acc, obj) => {
    if (obj.id) {
      return {
        ...acc,
        [obj.id]: (acc[obj.id] || 0) + 1,
      };
    }
    return acc;
  }, {});

  const hasIdDuplicates = Object.values(hasDuplications).some((count) => count > 1);

  if (updated.objects && hasIdDuplicates) {
    updated.objects = updated.objects.filter((obj) => {
      if (!obj || obj.type !== 'uYOImage') return true;

      const isEmptyUyo = !obj.src || obj.src === '';
      const backgroundColorFilled = obj.backgroundColor;

      return !(backgroundColorFilled && isEmptyUyo);
    });
  }
  return updated;
};

export const fabricDesignWithTextBorderObjectsRemoved = (design) => {
  if (!design) return design;
  const updated = {...design};
  design.backgroundImage = {...design.backgroundImage};
  if (updated.objects) {
    updated.objects = updated.objects.filter((obj) => {
      // biome-ignore lint/style/noParameterAssign: dont assign param
      obj = {...obj};
      return !(!obj || obj.type === 'textBorder');
    });
  }
  return fabricDesignRemoveDuplicateUyoEmptyImages(updated);
};

/**
 * Returns a copy of a single fabric design with corrected settings for cross-origin images
 * @param {Fabric Design Object} design
 * @return {Fabric Design Object}
 */
export const fabricDesignWithCrossOriginUpdates = (design) => {
  if (!design) return design;
  const updated = {...design};
  let d = design.backgroundImage;
  if (d) {
    d = {...d};
    if (d.originalUrl) d.originalUrl = httpsIfyUrl(d.originalUrl);
    if (d.src) d.src = d.src.replace('http:', 'https:');
    d.crossOrigin = 'anonymous';
    updated.backgroundImage = d;
  }

  if (updated.objects) {
    updated.objects = updated.objects.map((obj) => {
      // biome-ignore lint/style/noParameterAssign: dont assign param
      obj = {...obj};
      if (obj && ['image', 'uYOImage'].indexOf(obj.type) > -1) {
        if (obj.originalUrl) obj.originalUrl = httpsIfyUrl(obj.originalUrl);
        if (obj.src) obj.src = httpsIfyUrl(obj.src);
        obj.crossOrigin = 'anonymous';
      }
      return obj;
    });
  }
  return updated;
};

/**
 * Returns a copy of fabric_editable_art with corrected settings for cross-origin images for all categories
 *
 * @param {Fabric Editable Art Object} fabricArt
 * @return {Fabric Editable Art Object}
 */
export const fabricArtWithHttpsForAllImgPaths = (fabricArt) => {
  if (!fabricArt) return null;

  // Port all urls to https and add crossOrigin: anonymous
  const fabricArtByCategoryKeys = fabricArt.editable_art
    ? {...fabricArt.editable_art}
    : {...fabricArt};
  const updatedFabricArt = {...fabricArtByCategoryKeys};

  for (const k of Object.keys(updatedFabricArt)) {
    updatedFabricArt[k] = fabricDesignWithCrossOriginUpdates(updatedFabricArt[k]);
  }

  return updatedFabricArt;
};

/**
 * Converts a UTC date string into a timezone-aware date string
 * @param {*} dateStr the initial date string, in UTC
 * @param {*} timeZone the timezone to transform into
 * @returns string
 */
export const convertUTCDateToTzAwareDate = (dateStr, timeZone = '') => {
  const date = moment.utc(dateStr);
  if (!timeZone) return date.toString();
  return date.tz(timeZone).toString();
};

/**
 * Checks if the event's classification matches the given category
 * @param {*} eventClassification the event's classification
 * @param {*} category the category to validate against
 * @returns boolean
 */
export const doesMatchEventCategory = (eventClassification, category) => {
  if (!eventClassification) return false;
  const eventClassificationParts = eventClassification
    .split('-')
    .map((part) => part.replace(/\s*\(.*?\)/g, '').trim());
  return eventClassificationParts.some((part) => part.toLowerCase() === category.toLowerCase());
};

/**
 * Checks if the event's type matches any of the given event types
 * @param {string} eventType the event's type
 * @param {string|string[]} types the event type(s) to validate against
 * @returns boolean
 */
export const doesMatchEventType = (eventType, types) => {
  if (!eventType) return false;
  const typeArray = Array.isArray(types) ? types : [types];
  return typeArray.includes(eventType);
};

export function shouldShowMaybeRSVPFilter({origin, allowMaybeRSVPs, maybeRSVPCount}) {
  if (!isFabric(origin)) {
    return !isLegacyPremium(origin);
  }
  return allowMaybeRSVPs || (maybeRSVPCount && maybeRSVPCount > 0);
}
